import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

interface BottomSheetProps {
    open: boolean;
    onDismiss: () => void;
    title: string;
    content: ReactNode;
}

const OPEN_HEIGHT_RATIO = 0.4;
const CLOSE_HEIGHT_RATIO = 0.15;
const MAX_HEIGHT_RATIO = 1;

function getViewportHeight(): number {
    return window.visualViewport?.height ?? window.innerHeight;
}

function BottomSheet({open, onDismiss, title, content}: BottomSheetProps): JSX.Element | null {
    const [rendered, setRendered] = useState(open);
    const [height, setHeight] = useState(() => (typeof window !== 'undefined' ? getViewportHeight() * OPEN_HEIGHT_RATIO : 0));
    const [isDragging, setIsDragging] = useState(false);
    const [hasEntered, setHasEntered] = useState(false);

    const heightRef = useRef(height);
    const dragRef = useRef<{pointerId: number; startY: number; startHeight: number} | null>(null);
    const onDismissRef = useRef(onDismiss);

    useEffect(() => {
        heightRef.current = height;
    }, [height]);

    useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const openHeight = getViewportHeight() * OPEN_HEIGHT_RATIO;
        setRendered(true);
        setHasEntered(false);
        setHeight(openHeight);
        heightRef.current = openHeight;
        setIsDragging(false);
        dragRef.current = null;
    }, [open]);

    useEffect(() => {
        if (!rendered) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [rendered]);

    const closeImmediately = (): void => {
        dragRef.current = null;
        setIsDragging(false);
        setRendered(false);
        onDismissRef.current();
    };

    const finishAnimatedClose = (): void => {
        setRendered(false);
        setIsDragging(false);
        dragRef.current = null;
    };

    useEffect(() => {
        if (!isDragging) {
            return undefined;
        }

        const handlePointerMove = (event: PointerEvent): void => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) {
                return;
            }

            const maxHeight = getViewportHeight() * MAX_HEIGHT_RATIO;
            const nextHeight = Math.min(maxHeight, Math.max(0, drag.startHeight - (event.clientY - drag.startY)));
            setHeight(nextHeight);
            heightRef.current = nextHeight;
        };

        const handlePointerUp = (event: PointerEvent): void => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) {
                return;
            }

            dragRef.current = null;
            setIsDragging(false);

            const closeHeight = getViewportHeight() * CLOSE_HEIGHT_RATIO;
            if (heightRef.current < closeHeight) {
                closeImmediately();
            }
            // Otherwise keep the dragged height as-is (between 15% and 100%).
        };

        document.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', handlePointerUp);
        document.addEventListener('pointercancel', handlePointerUp);

        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
            document.removeEventListener('pointerup', handlePointerUp);
            document.removeEventListener('pointercancel', handlePointerUp);
        };
    }, [isDragging]);

    useEffect(() => {
        if (open || !rendered) {
            return;
        }

        if (heightRef.current < getViewportHeight() * CLOSE_HEIGHT_RATIO) {
            finishAnimatedClose();
        }
    }, [open, rendered]);

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!open || event.button !== 0) {
            return;
        }

        event.preventDefault();
        dragRef.current = {
            pointerId: event.pointerId,
            startY: event.clientY,
            startHeight: heightRef.current,
        };
        setIsDragging(true);
    };

    if (!rendered || typeof document === 'undefined') {
        return null;
    }

    const sheetClassName = [
        'c-bottomSheet',
        open ? '' : 'is-closing',
        isDragging ? 'is-dragging' : '',
        open && !hasEntered ? 'is-entering' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const closeHeight = getViewportHeight() * CLOSE_HEIGHT_RATIO;
    const backdropOpacity = open || isDragging ? 0.45 * Math.min(1, height / Math.max(closeHeight, 1)) : undefined;

    return createPortal(
        <div className={sheetClassName} role="dialog" aria-modal="true">
            <button
                type="button"
                className="c-bottomSheet__backdrop"
                aria-label="Dismiss"
                style={backdropOpacity === undefined ? undefined : {opacity: backdropOpacity, animation: 'none'}}
                onClick={() => onDismissRef.current()}
                onAnimationEnd={(event) => {
                    if (event.target !== event.currentTarget || open) {
                        return;
                    }
                    finishAnimatedClose();
                }}
            />
            <div
                className="c-bottomSheet__panel"
                style={{
                    height,
                    transition: isDragging ? 'none' : undefined,
                }}
                onAnimationEnd={(event) => {
                    if (event.target !== event.currentTarget) {
                        return;
                    }

                    if (!open) {
                        finishAnimatedClose();
                        return;
                    }

                    if (!hasEntered) {
                        setHasEntered(true);
                    }
                }}
            >
                <div className="c-bottomSheet__dragArea" onPointerDown={handlePointerDown}>
                    <div className="c-bottomSheet__handle" aria-hidden />
                    <div className="c-bottomSheet__title">{title}</div>
                </div>
                <div className="c-bottomSheet__content">{content}</div>
            </div>
        </div>,
        document.body
    );
}

export default BottomSheet;
